import React, { useState, useEffect, useRef } from 'react'
import { FlexGrid } from '@telus-uds/components-web'
import { ButtonLinkWrapperProps } from '../ButtonLinkWrapper/ButtonLinkWrapper'

import StickyCartNav from './StickyCartNav'
import StickyCartPlan from './StickyCartPlan'
import StickyCartHeader from './StickyCartHeader'
import Cart from './Cart'

import { MobileStickyCart } from './Styles'
import { IStickyCartProductDetails } from './StickyCartTypes'

export type StickyCartProps = {
  stickyCartRibbon?: string
  stickyCartRibbonWithCoupon?: string
  stickyCartPrice?: string
  stickyCartCouponPrice?: string
  stickyCartHeading?: string
  stickyCartSubHeading?: string
  stickyCartPriceSize?: 'small' | 'medium' | 'large'
  stickyCartRateText?: string
  stickyCartPriceSignDirection?: 'left' | 'right'
  strikePrice?: string
  strikeCouponPrice?: string
  strikePriceSize?: 'small' | 'medium' | 'large'
  strikePriceRateText?: string
  strikePriceSignDirection?: 'left' | 'right'
  topStickyCartContract?: string
  bottomStickyCartContract?: string
  stickyCartCallout?: string
  stickyCartPrimaryButtonText?: string
  stickyCartSecondaryButtonText?: string
  stickyCartPrimaryButtonHref?: string
  stickyCartCouponButtonHref?: string
  stickyCartSecondaryButtonHref?: string
  stickyNavRibbon?: string
  stickyNavRibbonWithCoupon?: string
  stickyNavCallout?: string
  stickyNavPrice?: string
  stickyNavCouponPrice?: string
  stickyNavPriceSize?: string
  stickyNavPriceRateText?: string
  stickyNavButtonHref?: string
  stickyNavCouponButtonHref?: string
  stickyNavButtonText?: string
  componentHeader?: string
  stickyCardsHeading?: string
  productDetails?: IStickyCartProductDetails[]
  priceToggle?: React.ReactNode
  priceToggleBlock?: JSX.Element
  heroImage?: React.ReactNode
  planCardSection?: React.ReactNode
  customButtonLinkNavProps?: ButtonLinkWrapperProps
  customButtonLinkCartProps?: ButtonLinkWrapperProps
  alignStickySecondaryButtonText?: 'left' | 'center' | 'right'
  cartSecondaryButton?: React.ReactNode
  cartSecondaryButtonFields?: boolean
  dropDownYearTerm?: React.ReactNode
  dropDownYearTermBlock?: JSX.Element
  primaryButtonHrefThreeYearTermWithCoupon?: string
  primaryButtonHrefThreeYearTermWithoutCoupon?: string
  primaryButtonHrefFiveYearTermWithCoupon?: string
  primaryButtonHrefFiveYearTermWithoutCoupon?: string
}

const StickyCart = ({
  stickyCartRibbon,
  stickyCartRibbonWithCoupon,
  stickyCartPrice,
  stickyCartCouponPrice,
  stickyCartHeading,
  stickyCartSubHeading,
  stickyCartPriceSize = 'medium',
  stickyCartRateText = '/month',
  stickyCartPriceSignDirection = 'left',
  strikePrice,
  strikeCouponPrice,
  strikePriceSize = 'small',
  strikePriceRateText = '/month',
  strikePriceSignDirection = 'left',
  topStickyCartContract,
  bottomStickyCartContract,
  stickyCartCallout,
  stickyCartPrimaryButtonText,
  stickyCartSecondaryButtonText,
  stickyCartPrimaryButtonHref,
  stickyCartCouponButtonHref,
  stickyCartSecondaryButtonHref,
  stickyNavRibbon,
  stickyNavRibbonWithCoupon,
  stickyNavCallout,
  stickyNavPrice,
  stickyNavCouponPrice,
  stickyNavPriceSize = 'medium',
  stickyNavPriceRateText = '/month',
  stickyNavButtonHref,
  stickyNavCouponButtonHref,
  stickyNavButtonText,
  componentHeader,
  stickyCardsHeading,
  productDetails,
  heroImage,
  priceToggle,
  priceToggleBlock,
  planCardSection,
  customButtonLinkNavProps,
  customButtonLinkCartProps,
  alignStickySecondaryButtonText,
  cartSecondaryButton,
  cartSecondaryButtonFields = false,
  dropDownYearTerm,
  dropDownYearTermBlock,
  primaryButtonHrefThreeYearTermWithCoupon,
  primaryButtonHrefThreeYearTermWithoutCoupon,
  primaryButtonHrefFiveYearTermWithCoupon,
  primaryButtonHrefFiveYearTermWithoutCoupon,
}: StickyCartProps) => {
  const largeBreakPoint = 992

  const isMobileRef = useRef(null)
  const [visible, setVisible] = useState(false)
  const [stickyCartInMobile, setStickyCartInMobile] = useState(false)
  const [stickyCartInDesktop, setStickyCartInDesktop] = useState(false)
  const [cartActive, setCartActive] = useState(false)
  const [active, setActive] = useState(undefined)
  const stickyRef = useRef<HTMLDivElement>(null)
  const mobileCartRef = useRef<HTMLDivElement>(null)

  const displayInMobile = () => {
    if (isMobileRef.current) {
      setStickyCartInMobile(true)
      setStickyCartInDesktop(false)
    } else {
      setStickyCartInMobile(false)
      setStickyCartInDesktop(true)
    }
  }

  useEffect(() => {
    const checkIfMobile = () => {
      isMobileRef.current = window.innerWidth < largeBreakPoint
      displayInMobile()
    }

    window.addEventListener('resize', checkIfMobile)

    checkIfMobile()
    displayInMobile()

    const currentObserver = new IntersectionObserver((entries) => {
      if (
        // if mobile breakpoint && only one entry exist && sticky cart is not intersecting
        (isMobileRef.current &&
          entries.length === 1 &&
          !entries[0].isIntersecting &&
          mobileCartRef.current.getBoundingClientRect().top < 0) ||
        // if desktop breakpoint && only one entry exist && sticky cart is not intersecting
        (!isMobileRef.current && entries.length === 1 && !entries[0].isIntersecting)
      ) {
        setVisible(true)
      } else {
        setVisible(false)
      }
    })

    if (mobileCartRef.current) {
      currentObserver.observe(mobileCartRef.current)
    }

    if (stickyRef.current) {
      currentObserver.observe(stickyRef.current)
    }
  }, [])

  useEffect(() => {
    if (customButtonLinkNavProps && customButtonLinkNavProps['buttonLinkProps']) {
      customButtonLinkNavProps['buttonLinkProps']['url'] = cartActive ? stickyNavCouponButtonHref : stickyNavButtonHref
    }
  }, [cartActive])

  return (
    <section data-testid={'stickyCart-testid'}>
      <StickyCartNav
        stickyNavRibbon={stickyNavRibbon}
        stickyNavRibbonWithCoupon={stickyNavRibbonWithCoupon}
        stickyNavCallout={stickyNavCallout}
        stickyNavPrice={stickyNavPrice}
        stickyNavCouponPrice={stickyNavCouponPrice}
        stickyNavPriceSize={stickyNavPriceSize}
        stickyNavPriceRateText={stickyNavPriceRateText}
        stickyNavButtonHref={stickyNavButtonHref}
        stickyNavCouponButtonHref={stickyNavCouponButtonHref}
        stickyNavButtonText={stickyNavButtonText}
        customButtonLinkNavProps={customButtonLinkNavProps}
        visible={visible}
        cartActive={cartActive}
      />
      <FlexGrid gutter={false}>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} lg={7}>
            <StickyCartHeader componentHeader={componentHeader} heroImage={heroImage} />
            <MobileStickyCart ref={mobileCartRef} stickyCartInMobile={stickyCartInMobile} data-testid={'mobileCart-id'}>
              <Cart
                setActive={setActive}
                active={active}
                stickyCartInMobile={stickyCartInMobile}
                stickyCartRibbon={stickyCartRibbon}
                stickyCartRibbonWithCoupon={stickyCartRibbonWithCoupon}
                stickyCartPrice={stickyCartPrice}
                stickyCartCouponPrice={stickyCartCouponPrice}
                stickyCartPriceSize={stickyCartPriceSize}
                stickyCartRateText={stickyCartRateText}
                stickyCartPriceSignDirection={stickyCartPriceSignDirection}
                strikePrice={strikePrice}
                strikeCouponPrice={strikeCouponPrice}
                strikePriceSize={strikePriceSize}
                strikePriceRateText={strikePriceRateText}
                strikePriceSignDirection={strikePriceSignDirection}
                topStickyCartContract={topStickyCartContract}
                bottomStickyCartContract={bottomStickyCartContract}
                stickyCartCallout={stickyCartCallout}
                stickyCartPrimaryButtonText={stickyCartPrimaryButtonText}
                stickyCartSecondaryButtonText={stickyCartSecondaryButtonText}
                stickyCartPrimaryButtonHref={stickyCartPrimaryButtonHref}
                stickyCartCouponButtonHref={stickyCartCouponButtonHref}
                stickyCartSecondaryButtonHref={stickyCartSecondaryButtonHref}
                productDetails={productDetails}
                stickyCartHeading={stickyCartHeading}
                stickyCartSubHeading={stickyCartSubHeading}
                stickyRef={undefined}
                stickyCartInDesktop={false}
                priceToggle={priceToggle}
                priceToggleBlock={priceToggleBlock}
                setCartActive={setCartActive}
                customButtonLinkCartProps={customButtonLinkCartProps}
                alignStickySecondaryButtonText={alignStickySecondaryButtonText}
                cartSecondaryButton={cartSecondaryButton}
                cartSecondaryButtonFields={cartSecondaryButtonFields}
                dropDownYearTerm={dropDownYearTerm}
                dropDownYearTermBlock={dropDownYearTermBlock}
                primaryButtonHrefThreeYearTermWithCoupon={primaryButtonHrefThreeYearTermWithCoupon}
                primaryButtonHrefThreeYearTermWithoutCoupon={primaryButtonHrefThreeYearTermWithoutCoupon}
                primaryButtonHrefFiveYearTermWithCoupon={primaryButtonHrefFiveYearTermWithCoupon}
                primaryButtonHrefFiveYearTermWithoutCoupon={primaryButtonHrefFiveYearTermWithoutCoupon}
              />
            </MobileStickyCart>
            <StickyCartPlan stickyCardsHeading={stickyCardsHeading} planCardSection={planCardSection} />
          </FlexGrid.Col>
          <FlexGrid.Col lg={5}>
            <Cart
              data-testid={'desktopCart-id'}
              active={active}
              setActive={setActive}
              stickyCartInDesktop={stickyCartInDesktop}
              stickyRef={stickyRef}
              stickyCartRibbon={stickyCartRibbon}
              stickyCartRibbonWithCoupon={stickyCartRibbonWithCoupon}
              stickyCartPrice={stickyCartPrice}
              stickyCartCouponPrice={stickyCartCouponPrice}
              stickyCartPriceSize={stickyCartPriceSize}
              stickyCartRateText={stickyCartRateText}
              stickyCartPriceSignDirection={stickyCartPriceSignDirection}
              strikePrice={strikePrice}
              strikeCouponPrice={strikeCouponPrice}
              strikePriceSize={strikePriceSize}
              strikePriceRateText={strikePriceRateText}
              strikePriceSignDirection={strikePriceSignDirection}
              topStickyCartContract={topStickyCartContract}
              bottomStickyCartContract={bottomStickyCartContract}
              stickyCartCallout={stickyCartCallout}
              stickyCartPrimaryButtonText={stickyCartPrimaryButtonText}
              stickyCartSecondaryButtonText={stickyCartSecondaryButtonText}
              stickyCartPrimaryButtonHref={stickyCartPrimaryButtonHref}
              stickyCartCouponButtonHref={stickyCartCouponButtonHref}
              stickyCartSecondaryButtonHref={stickyCartSecondaryButtonHref}
              productDetails={productDetails}
              stickyCartHeading={stickyCartHeading}
              stickyCartSubHeading={stickyCartSubHeading}
              priceToggle={priceToggle}
              priceToggleBlock={priceToggleBlock}
              setCartActive={setCartActive}
              customButtonLinkCartProps={customButtonLinkCartProps}
              alignStickySecondaryButtonText={alignStickySecondaryButtonText}
              cartSecondaryButton={cartSecondaryButton}
              cartSecondaryButtonFields={cartSecondaryButtonFields}
              dropDownYearTerm={dropDownYearTerm}
              dropDownYearTermBlock={dropDownYearTermBlock}
              primaryButtonHrefThreeYearTermWithCoupon={primaryButtonHrefThreeYearTermWithCoupon}
              primaryButtonHrefThreeYearTermWithoutCoupon={primaryButtonHrefThreeYearTermWithoutCoupon}
              primaryButtonHrefFiveYearTermWithCoupon={primaryButtonHrefFiveYearTermWithCoupon}
              primaryButtonHrefFiveYearTermWithoutCoupon={primaryButtonHrefFiveYearTermWithoutCoupon}
            />
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>
    </section>
  )
}

export default StickyCart
